import { skipToken } from "@reduxjs/toolkit/query";
import {
  ChatContextPill,
  chatContextPillIconClassName,
  chatContextPillMarkClassName,
} from "@repo/ui/chat/chatContextPill";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { IconBook2, IconUserBolt } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import type { ResearchAgentThreadMetadata } from "./researchAgentContext.js";

export interface ResearchAgentContextPillProps {
  /**
   * The metadata of the thread being composed into — the selected thread's own
   * scope, or the current route's for a thread that hasn't been created yet.
   */
  threadMetadata: ResearchAgentThreadMetadata;
}

/** Shown when the scoped entity is gone (deleted, or no longer visible). */
const UNKNOWN_TITLE = "Unknown";

/**
 * Names the playbook or voice agent the chat is scoped to.
 *
 * This is the whole of the context the research agent is handed without being
 * asked: the thread's metadata travels along as `requestContext`, and decides
 * both the agent's prompt and which tools it gets (see `researchAgent.ts` in
 * `agent-api`). The redesigned header shows only the thread title, so without
 * this a manager spanning several playbooks can't tell what a question will be
 * answered against.
 *
 * The name stands alone as the pill's title — the icon carries which kind of
 * thing it is, matching how the app already marks playbooks and voice agents
 * in navigation and search.
 */
export const ResearchAgentContextPill: FC<ResearchAgentContextPillProps> = ({
  threadMetadata,
}) => {
  const { teammateAgentId, playbookId } = threadMetadata;

  const { data: teammateAgent, isLoading: isTeammateAgentLoading } =
    useGetTeammateAgentQuery(teammateAgentId ?? skipToken);

  // The minimal list is fetched by the app anyway and covers every playbook,
  // including the ones behind voice agents; `playbookById` would pull a whole
  // assessment tree to read one name.
  const { data: playbooks, isLoading: arePlaybooksLoading } =
    useAllMinimalPlaybooksQuery(teammateAgentId ? skipToken : undefined);

  if (teammateAgentId) {
    return (
      <ChatContextPill
        icon={
          <span className={chatContextPillMarkClassName}>
            <IconUserBolt
              aria-hidden
              className={chatContextPillIconClassName}
            />
          </span>
        }
        title={
          isTeammateAgentLoading ? (
            <ContextPillTitleSkeleton />
          ) : (
            teammateAgent?.name || UNKNOWN_TITLE
          )
        }
      />
    );
  }

  const playbook = playbooks?.find((candidate) => candidate.id === playbookId);

  return (
    <ChatContextPill
      icon={
        <span className={chatContextPillMarkClassName}>
          <IconBook2 aria-hidden className={chatContextPillIconClassName} />
        </span>
      }
      title={
        arePlaybooksLoading ? (
          <ContextPillTitleSkeleton />
        ) : (
          playbook?.name || UNKNOWN_TITLE
        )
      }
    />
  );
};

/**
 * Holds the pill's line while the name loads. The pill sits above the composer
 * input, so rendering nothing until the name arrives would grow the composer
 * under the user's cursor.
 */
const ContextPillTitleSkeleton: FC = (): ReactNode => (
  <span className="flex h-4 items-center">
    <span className={skeletonStyle({ className: "h-2 w-24" })} />
    <span className="sr-only">Loading chat context...</span>
  </span>
);
